import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Package,
  AlertCircle,
  RefreshCw,
  Loader,
  Info,
  Trash2,
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { purchaseOrderLinesService } from '../../../../services/purchaseOrderLinesService';
import { pOrdersService } from '../../../../services/pOrdersService';
import {
  POrderLineWithHeaderDto,
} from '../../../../types/purchaseOrderLines';
import { PurchaseOrderLineDetailsModal } from './components/PurchaseOrderLineDetailsModal';

// Import the reusable warehouse table system
import { WarehouseTable, createColumnFactory } from '../../../shared/table';
import type { ColumnDefinition, TableDataRow } from '../../../shared/table';

// Table row type for purchase order lines
interface POrderLineTableRow extends TableDataRow {
  id: number;
  orderNo: number;
  orderNoERP: string;
  lineNo: number;
  lineNoERP: number | null;
  lineStatus: number;
  lineStatusText: string;
  orderStatus: number;
  orderStatusText: string;
  itemNo: string;
  itemBarCode: string | null;
  description1: string | null;
  supplierNo: string;
  supplierName: string | null;
  quantity: number | null;
  quantityDelivered: number | null;
  remainingQuantity: number;
  completionPct: number;
  itemUnitCode: string | null;
  price: number | null;
  discount: number | null;
  orderDate: string | null;
  deliveryDate: string | null;
  orderDeliveryDate: string | null;
  warehouse: string | null;
  stdLocationName: string | null;
  batchItemYesNo: boolean;
  serialNoItemYesNo: boolean;
  itemBatchNo: string | null;
  inboundShipmentNo: string | null;
  yourRef: string | null;
  ourRef: string | null;
  supplierOrderNo: string | null;
  carrierName: string | null;
  contactName: string | null;
  deliveryMethod: string | null;
  itemGroup1: string | null;
  itemGroup2: string | null;
  itemGroup3: string | null;
  lineCreated: string | null;
  lineLastUpdate: string | null;
  _original: POrderLineWithHeaderDto;
}

// Create column factory
const columnFactory = createColumnFactory<POrderLineTableRow>();

interface PurchaseOrderLinesListProps {
  onBack?: () => void;
}

export const PurchaseOrderLinesList: React.FC<PurchaseOrderLinesListProps> = ({ onBack }) => {
  const [lines, setLines] = useState<POrderLineWithHeaderDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLine, setSelectedLine] = useState<POrderLineWithHeaderDto | null>(null);
  const [selectedLineData, setSelectedLineData] = useState<POrderLineTableRow[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ orderNo: number; lineNo: number; itemNo: string; description: string; orderStatus: number } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all pages (API max pageSize is 500)
      const PAGE_SIZE = 500;
      let allLines: POrderLineWithHeaderDto[] = [];
      let pageNumber = 1;
      let hasMore = true;

      while (hasMore) {
        const result = await purchaseOrderLinesService.getPOrderLinesPaged({
          pageNumber,
          pageSize: PAGE_SIZE,
          sortColumn: 'OrderNo',
          sortDirection: 'desc',
          maxOrderStatus: 50,
        });
        allLines = [...allLines, ...result.items];
        hasMore = result.hasNextPage;
        pageNumber++;
      }

      setLines(allLines);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'En feil oppstod ved lasting av data');
      console.error('Error loading purchase order lines:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Selection change handler
  const handleSelectionChange = useCallback((_selectedRows: (string | number)[], selectedData: POrderLineTableRow[]) => {
    setSelectedLineData(selectedData);
  }, []);

  // Delete order line handler
  const handleDeleteOrderLine = useCallback(async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await pOrdersService.deleteOrderLine(deleteConfirm.orderNo, deleteConfirm.lineNo);
      setDeleteConfirm(null);
      setSelectedLineData([]);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feil ved sletting av bestillingslinje');
    } finally {
      setDeleting(false);
    }
  }, [deleteConfirm, loadData]);

  // Status color helper
  const getStatusColor = (status: number): string => {
    switch (status) {
      case 0: return 'bg-gray-100 text-gray-800';
      case 10: return 'bg-green-100 text-green-800';
      case 20: return 'bg-blue-100 text-blue-800';
      case 30: return 'bg-yellow-100 text-yellow-800';
      case 40: return 'bg-green-100 text-green-800';
      case 45: return 'bg-purple-100 text-purple-800';
      case 50: return 'bg-indigo-100 text-indigo-800';
      case 60: return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Transform to table data
  const tableData: POrderLineTableRow[] = useMemo(() => {
    return lines.map((line) => {
      const qty = line.quantity ?? 0;
      const delivered = line.quantityDelivered ?? 0;
      const completionPct = qty > 0 ? Math.min(Math.round((delivered / qty) * 100), 100) : 0;

      return {
        id: line.id,
        orderNo: line.orderNo,
        orderNoERP: line.orderNoERP || line.orderNo.toString(),
        lineNo: line.lineNo,
        lineNoERP: line.lineNoERP ?? null,
        lineStatus: line.lineStatus,
        lineStatusText: line.lineStatusText || '',
        orderStatus: line.orderStatus,
        orderStatusText: line.orderStatusText || '',
        itemNo: line.itemNo,
        itemBarCode: line.itemBarCode || null,
        description1: line.description1 || null,
        supplierNo: line.supplierNo,
        supplierName: line.supplierName || null,
        quantity: line.quantity ?? null,
        quantityDelivered: line.quantityDelivered ?? null,
        remainingQuantity: line.remainingQuantity,
        completionPct,
        itemUnitCode: line.itemUnitCode || null,
        price: line.price ?? null,
        discount: line.discount ?? null,
        orderDate: line.orderDate || null,
        deliveryDate: line.deliveryDate || null,
        orderDeliveryDate: line.orderDeliveryDate || null,
        warehouse: line.warehouse || null,
        stdLocationName: line.stdLocationName || null,
        batchItemYesNo: line.batchItemYesNo ?? false,
        serialNoItemYesNo: line.serialNoItemYesNo ?? false,
        itemBatchNo: line.itemBatchNo || null,
        inboundShipmentNo: line.inboundShipmentNo || null,
        yourRef: line.yourRef || null,
        ourRef: line.ourRef || null,
        supplierOrderNo: line.supplierOrderNo || null,
        carrierName: line.carrierName || null,
        contactName: line.contactName || null,
        deliveryMethod: line.deliveryMethod || null,
        itemGroup1: line.itemGroup1 || null,
        itemGroup2: line.itemGroup2 || null,
        itemGroup3: line.itemGroup3 || null,
        lineCreated: line.lineCreated || null,
        lineLastUpdate: line.lineLastUpdate || null,
        _original: line,
      };
    });
  }, [lines]);

  // Row ID function
  const getRowId = useCallback((row: POrderLineTableRow) => row.id, []);

  // Info cell renderer
  const renderInfoCell = useCallback((_value: any, row: POrderLineTableRow) => {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setSelectedLine(row._original);
        }}
        className="p-1.5 rounded transition-colors hover:bg-blue-100 text-blue-500 hover:text-blue-700"
        title="Vis detaljer"
      >
        <Info size={14} />
      </button>
    );
  }, []);

  // Column definitions
  const columns: ColumnDefinition<POrderLineTableRow>[] = useMemo(() => [
    // === INFO-KNAPP ===
    columnFactory.createTextColumn('info', 'id', '', {
      width: 40,
      sortable: false,
      filterType: 'none',
      alignment: 'center',
      renderer: renderInfoCell,
    }),
    // === SYNLIGE HOVEDFELTER ===
    columnFactory.createTextColumn('orderNoERP', 'orderNoERP', 'Ordre (ERP)', {
      width: 110,
      sortable: true,
      filterType: 'both',
    }),
    columnFactory.createNumberColumn('lineNo', 'lineNo', 'Linjenr', {
      width: 70,
      sortable: true,
      filterType: 'both',
    }),
    columnFactory.createTextColumn('itemNo', 'itemNo', 'Varenr.', {
      width: 120,
      sortable: true,
      filterType: 'both',
    }),
    columnFactory.createTextColumn('description1', 'description1', 'Beskrivelse', {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
    }),
    columnFactory.createTextColumn('supplierName', 'supplierName', 'Leverandør', {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
    }),
    columnFactory.createNumberColumn('quantity', 'quantity', 'Antall', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toFixed(2) : '-',
    }),
    columnFactory.createNumberColumn('quantityDelivered', 'quantityDelivered', 'Levert', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toFixed(2) : '-',
    }),
    columnFactory.createNumberColumn('remainingQuantity', 'remainingQuantity', 'Gjenstår', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== undefined && value !== null ? value.toFixed(2) : '-',
    }),
    columnFactory.createNumberColumn('completionPct', 'completionPct', 'Framdrift', {
      width: 110,
      sortable: true,
      filterType: 'both',
      renderer: (value) => {
        const pct = value ?? 0;
        const barColor = pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-blue-500' : 'bg-orange-500';
        return (
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-[60px]">
              <div
                className={`h-2 rounded-full ${barColor}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
            <span className="text-xs text-gray-600 whitespace-nowrap">{pct}%</span>
          </div>
        );
      },
    }),
    columnFactory.createDateColumn('orderDate', 'orderDate', 'Ordredato', {
      width: 100,
      sortable: true,
      filterType: 'both',
    }),
    columnFactory.createDateColumn('deliveryDate', 'deliveryDate', 'Leveringsdato', {
      width: 100,
      sortable: true,
      filterType: 'both',
    }),
    columnFactory.createTextColumn('orderStatusText', 'orderStatusText', 'Ordrestatus', {
      width: 130,
      sortable: true,
      filterType: 'dropdown',
      renderer: (value, row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(row.orderStatus)}`}>
          {value}
        </span>
      ),
    }),

    // === SKJULTE EKSTRAFELTER ===
    columnFactory.createTextColumn('supplierNo', 'supplierNo', 'Leverandørnr', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false,
    }),
    columnFactory.createNumberColumn('price', 'price', 'Pris', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toFixed(2) : '-',
      visible: false,
    }),
    columnFactory.createNumberColumn('discount', 'discount', 'Rabatt %', {
      width: 80,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? `${value.toFixed(1)}%` : '-',
      visible: false,
    }),
    columnFactory.createTextColumn('itemUnitCode', 'itemUnitCode', 'Enhet', {
      width: 70,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('warehouse', 'warehouse', 'Lager', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('stdLocationName', 'stdLocationName', 'Stdlokasjon', {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('itemBarCode', 'itemBarCode', 'Strekkode', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('itemBatchNo', 'itemBatchNo', 'Batchnr.', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('inboundShipmentNo', 'inboundShipmentNo', 'Forsendelsesnr', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('yourRef', 'yourRef', 'Deres ref', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('ourRef', 'ourRef', 'Vår ref', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('supplierOrderNo', 'supplierOrderNo', 'Lev. ordrenr', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('carrierName', 'carrierName', 'Transportør', {
      width: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('contactName', 'contactName', 'Kontaktperson', {
      width: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('deliveryMethod', 'deliveryMethod', 'Leveringsmåte', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('itemGroup1', 'itemGroup1', 'Varegruppe 1', {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('itemGroup2', 'itemGroup2', 'Varegruppe 2', {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createTextColumn('itemGroup3', 'itemGroup3', 'Varegruppe 3', {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false,
    }),
    columnFactory.createDateColumn('lineCreated', 'lineCreated', 'Opprettet', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false,
    }),
    columnFactory.createDateColumn('lineLastUpdate', 'lineLastUpdate', 'Sist oppdatert', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false,
    }),
    columnFactory.createNumberColumn('lineNoERP', 'lineNoERP', 'ERP Linjenr', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false,
    }),
    columnFactory.createDateColumn('orderDeliveryDate', 'orderDeliveryDate', 'Ordre lev.dato', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false,
    }),
    columnFactory.createTextColumn('lineStatusText', 'lineStatusText', 'Linjestatus', {
      width: 120,
      sortable: true,
      filterType: 'dropdown',
      renderer: (value, row) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(row.lineStatus)}`}>
          {value}
        </span>
      ),
      visible: false,
    }),
  ], []);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: 'Oppdater',
      icon: RefreshCw,
      onClick: loadData,
      variant: 'secondary' as const,
    },
  ], [loadData]);

  // Derived: selected line and whether it can be deleted
  const selectedLineRow = selectedLineData[0] ?? null;
  const canDeleteSelectedLine = selectedLineRow !== null && selectedLineRow.orderStatus < 60;

  // Toolbar left content
  const toolbarLeft = useMemo(() => (
    <div className="flex items-center gap-4">
      <div className="text-sm text-gray-600 font-medium">
        {tableData.length} bestillingslinjer
      </div>
      <button
        onClick={() => {
          if (selectedLineRow) {
            setDeleteConfirm({
              orderNo: selectedLineRow.orderNo,
              lineNo: selectedLineRow.lineNo,
              itemNo: selectedLineRow.itemNo,
              description: selectedLineRow.description1 || '',
              orderStatus: selectedLineRow.orderStatus
            });
          }
        }}
        disabled={!canDeleteSelectedLine}
        className={`px-3 py-1.5 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-all duration-200 ${
          canDeleteSelectedLine
            ? 'text-red-700 bg-white border border-red-300 hover:bg-red-50 hover:shadow-md cursor-pointer'
            : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
        }`}
        title={
          !selectedLineRow
            ? 'Velg en bestillingslinje for å slette'
            : !canDeleteSelectedLine
            ? 'Kan ikke slette linjer fra en eksportert bestilling'
            : `Slett linje ${selectedLineRow.lineNo} (${selectedLineRow.itemNo})`
        }
      >
        <Trash2 size={14} />
        <span>Slett linje</span>
      </button>
    </div>
  ), [tableData.length, selectedLineRow, canDeleteSelectedLine]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center space-x-4 mb-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <Package size={24} />
            <span>Bestillingslinjer</span>
          </h1>
        </div>
      </div>

      {/* Table content */}
      <div className="flex-1 min-h-0 px-4 py-2">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-2">
              <Loader className="animate-spin" size={20} />
              <span>Laster bestillingslinjer...</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
            <h3 className="text-red-800 font-medium flex items-center">
              <AlertCircle size={20} className="mr-2" />
              Feil ved lasting av bestillingslinjer
            </h3>
            <p className="text-red-600 text-sm mt-1">{error}</p>
            <button
              onClick={loadData}
              className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
            >
              <RefreshCw size={16} />
              <span>Prøv igjen</span>
            </button>
          </div>
        ) : (
          <WarehouseTable
            data={tableData}
            columns={columns}
            config={{
              showPagination: true,
              showColumnConfig: true,
              showGlobalSearch: true,
              showFilters: true,
              showExport: false,
              showSummary: false,
              defaultPageSize: 50,
              allowColumnReorder: true,
              allowColumnResize: true,
              stickyHeader: true,
              compactMode: true,
              persistenceKey: 'purchase-order-lines-table',
              allowRowSelection: { mode: 'single', selectAllMode: 'current-page' }
            }}
            getRowId={getRowId}
            onSelectionChange={handleSelectionChange}
            tableActions={tableActions}
            toolbarLeft={toolbarLeft}
            loading={false}
            error={undefined}
            aria-label="Bestillingslinjer tabell"
          />
        )}
      </div>

      {/* Details Modal */}
      {selectedLine && (
        <PurchaseOrderLineDetailsModal
          line={selectedLine}
          onClose={() => setSelectedLine(null)}
        />
      )}

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b flex items-center space-x-3">
              <AlertCircle className="text-red-600" size={24} />
              <h2 className="text-lg font-bold text-gray-900">Bekreft sletting</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700">
                Er du sikker på at du vil slette bestillingslinje <span className="font-semibold">{deleteConfirm.lineNo}</span> fra ordre {deleteConfirm.orderNo}?
              </p>
              {deleteConfirm.itemNo && (
                <p className="text-sm text-gray-500 mt-1">
                  Vare: {deleteConfirm.itemNo}{deleteConfirm.description ? ` - ${deleteConfirm.description}` : ''}
                </p>
              )}
              <p className="text-sm text-red-600 mt-2">
                Denne handlingen kan ikke angres.
              </p>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                onClick={handleDeleteOrderLine}
                disabled={deleting}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    <span>Sletter...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    <span>Slett</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
